// hooks/useOffline.ts — Network connectivity state hook

import { useState, useEffect, useCallback } from 'react';
import { flushSyncQueue, getSyncQueueCount } from '@/services/offline/syncQueue';

interface UseOfflineReturn {
  /** true when navigator.onLine is true */
  isOnline: boolean;
  /** true when we just came back online and are flushing the queue */
  isSyncing: boolean;
  /** Number of pending mutations waiting to be synced */
  pendingCount: number;
  /** Manually trigger a sync flush */
  triggerSync: () => Promise<void>;
}

/**
 * useOffline — tracks network connectivity and triggers sync queue flush on reconnect.
 *
 * @example
 * const { isOnline, isSyncing, pendingCount } = useOffline();
 * if (!isOnline) return <OfflineBanner />;
 */
export function useOffline(): UseOfflineReturn {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Update pending count from IndexedDB
  const refreshPendingCount = useCallback(async () => {
    try {
      const count = await getSyncQueueCount();
      setPendingCount(count);
    } catch {
      // IndexedDB may not be available in all environments
    }
  }, []);

  // Trigger a manual sync flush
  const triggerSync = useCallback(async () => {
    if (!navigator.onLine || isSyncing) return;

    setIsSyncing(true);
    try {
      await flushSyncQueue();
      await refreshPendingCount();
    } catch (err) {
      console.warn('[useOffline] Sync flush failed:', err);
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, refreshPendingCount]);

  useEffect(() => {
    // Initialize pending count
    void refreshPendingCount();

    const handleOnline = () => {
      setIsOnline(true);
      // Auto-flush sync queue when coming back online
      void (async () => {
        setIsSyncing(true);
        try {
          await flushSyncQueue();
          await refreshPendingCount();
        } catch (err) {
          console.warn('[useOffline] Auto-sync on reconnect failed:', err);
        } finally {
          setIsSyncing(false);
        }
      })();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [refreshPendingCount]);

  return { isOnline, isSyncing, pendingCount, triggerSync };
}
